<template>
  <div class="tool-page">
    <div class="tool-header">
      <h1>{{ $t('uppercase.title') }}</h1>
      <p>{{ $t('uppercase.subtitle') }}</p>
    </div>

    <div class="tool-card">
      <div class="input-section">
        <label>{{ $t('uppercase.inputLabel') }}</label>
        <div class="input-wrapper">
          <span class="currency-symbol">¥</span>
          <input 
            type="text" 
            v-model="amount" 
            @input="handleInput"
            @keypress="validateKeypress"
            :placeholder="$t('uppercase.inputPlaceholder')"
            inputmode="decimal"
          >
        </div>
      </div>

      <div class="result-section">
        <label>{{ $t('uppercase.outputLabel') }}</label>
        <div class="result-box">
          <span class="result-text">{{ result || $t('uppercase.waiting') }}</span>
          <button class="copy-btn" @click="copyResult" :disabled="!result" title="复制">
            📋
          </button>
        </div>
      </div>

      <div v-if="message" class="alert" :class="messageType">{{ message }}</div>

      <div class="actions">
        <button class="btn" @click="clearAll">{{ $t('common.clear') }}</button>
      </div>

      <div class="examples-section">
        <h3>{{ $t('uppercase.quickExamples') }}</h3>
        <div class="examples-grid">
          <div class="example-item" v-for="example in examples" :key="example.value" @click="useExample(example.value)">
            <span class="example-number">{{ example.label }}</span>
            <span class="example-result">{{ example.result }}</span>
          </div>
        </div>
      </div>

      <div class="info-section">
        <h3>{{ $t('uppercase.digitTable') }}</h3>
        <div class="table-wrapper">
          <table>
            <thead>
              <tr>
                <th>{{ $t('uppercase.arabicDigit') }}</th>
                <th v-for="n in 10" :key="n">{{ n - 1 }}</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>{{ $t('uppercase.chineseUppercase') }}</td>
                <td v-for="(char, index) in upperDigits" :key="index">{{ char }}</td>
              </tr>
            </tbody>
          </table>
        </div>
        <div class="unit-info">
          <p><strong>{{ $t('uppercase.unitInfo') }}：</strong>{{ $t('uppercase.unitValues') }}</p>
          <p><strong>{{ $t('uppercase.amountUnit') }}：</strong>{{ $t('uppercase.amountUnitValues') }}</p>
        </div>
      </div>

      <div class="tips-section">
        <h3>{{ $t('uppercase.usageTips') }}</h3>
        <ul>
          <li v-for="(tip, index) in $t('uppercase.tips')" :key="index">{{ tip }}</li>
        </ul>
      </div>
    </div>

    <!-- 工具说明 -->
    <div class="tool-description">
      <h2>{{ $t('uppercase.whyUppercase') }}</h2>
      <p>{{ $t('uppercase.whyUppercaseDesc') }}</p>

      <h2>{{ $t('uppercase.uppercaseHistory') }}</h2>
      <p>{{ $t('uppercase.uppercaseHistoryDesc') }}</p>

      <h2>{{ $t('uppercase.writingRules') }}</h2>
      <ul>
        <li v-for="(item, index) in $t('uppercase.writingRulesItems')" :key="'rule'+index">{{ item }}</li>
      </ul>

      <h2>{{ $t('uppercase.commonMistakes') }}</h2>
      <ul>
        <li v-for="(item, index) in $t('uppercase.commonMistakesItems')" :key="'mistake'+index">{{ item }}</li>
      </ul>

      <h2>{{ $t('uppercase.uppercaseExamples') }}</h2>
      <ul>
        <li v-for="(item, index) in $t('uppercase.uppercaseExamplesItems')" :key="'example'+index">{{ item }}</li>
      </ul>
    </div>
  </div>
</template>

<script>
export default {
  name: 'UppercaseConverter',
  data() {
    return {
      amount: '',
      result: '',
      message: '',
      messageType: 'success',
      upperDigits: ['零', '壹', '贰', '叁', '肆', '伍', '陆', '柒', '捌', '玖'],
      examples: [
        { value: '100', label: '100', result: '壹佰元整' },
        { value: '1234.56', label: '1234.56', result: '壹仟贰佰叁拾肆元伍角陆分' },
        { value: '10000', label: '10000', result: '壹万元整' },
        { value: '100000000', label: '1亿', result: '壹亿元整' }
      ]
    }
  },
  methods: {
    // 限制键盘输入：只允许数字、小数点、退格、删除等
    validateKeypress(event) {
      const char = event.key
      const currentValue = this.amount
      
      // 允许控制键（不在这里处理，让浏览器默认行为）
      if (event.ctrlKey || event.metaKey) return
      
      // 只允许数字和小数点
      if (!/[\d.]/.test(char)) {
        event.preventDefault()
        return
      }
      
      // 限制只能有一个小数点
      if (char === '.' && currentValue.includes('.')) {
        event.preventDefault()
        return
      }
      
      // 限制小数位数不超过2位
      if (currentValue.includes('.')) {
        const decimalPart = currentValue.split('.')[1]
        if (decimalPart && decimalPart.length >= 2 && char !== '.') {
          event.preventDefault()
          return
        }
      }
    },
    
    // 处理输入（包括粘贴等情况）
    handleInput() {
      // 过滤非法字符，只保留数字和小数点
      let value = this.amount.replace(/[^\d.]/g, '')
      
      // 处理多个小数点的情况，只保留第一个
      const parts = value.split('.')
      if (parts.length > 2) {
        value = parts[0] + '.' + parts.slice(1).join('')
      }
      
      // 限制小数位数为2位
      if (parts.length === 2 && parts[1].length > 2) {
        value = parts[0] + '.' + parts[1].substring(0, 2)
      }
      
      // 更新值
      if (value !== this.amount) {
        this.amount = value
      }
      
      // 执行转换
      this.convertToUppercase()
    },

    convertToUppercase() {
      const input = this.amount.trim()
      
      if (!input) {
        this.result = ''
        return
      }

      // 验证输入格式
      if (!/^\d+(\.\d{0,2})?$/.test(input)) {
        this.result = ''
        this.showMessage(this.$t('uppercase.invalidFormat'), 'error')
        return
      }

      const num = parseFloat(input)
      
      if (num < 0) {
        this.result = ''
        this.showMessage(this.$t('uppercase.negativeError'), 'error')
        return
      }

      if (num > 9999999999999.99) {
        this.result = ''
        this.showMessage(this.$t('uppercase.overflowError'), 'error')
        return
      }

      if (num === 0) {
        this.result = this.$t('uppercase.zero')
        this.message = ''
        return
      }

      this.result = this.numberToUppercase(num)
      this.message = ''
    },

    numberToUppercase(num) {
      const digits = ['零', '壹', '贰', '叁', '肆', '伍', '陆', '柒', '捌', '玖']
      const units = ['', '拾', '佰', '仟']
      const bigUnits = ['', '万', '亿', '万亿']
      
      // 分离整数和小数部分
      const [intPart, decPart = ''] = num.toFixed(2).split('.')
      
      let result = ''
      
      // 处理整数部分
      if (parseInt(intPart) === 0) {
        // 整数部分为0，不输出
      } else {
        const intStr = intPart
        const len = intStr.length
        let zeroFlag = false // 是否需要加零
        
        for (let i = 0; i < len; i++) {
          const digit = parseInt(intStr[i])
          const pos = len - i - 1 // 当前位置（从右往左数，0开始）
          const unitPos = pos % 4 // 在当前节内的位置
          const bigUnitPos = Math.floor(pos / 4) // 大单位位置
          
          if (digit === 0) {
            zeroFlag = true
            // 如果是节的最后一位且节不为0，加大单位
            if (unitPos === 0 && result && !result.endsWith(bigUnits[bigUnitPos])) {
              result += bigUnits[bigUnitPos]
            }
          } else {
            if (zeroFlag && result) {
              result += '零'
            }
            zeroFlag = false
            result += digits[digit] + units[unitPos]
            if (unitPos === 0) {
              result += bigUnits[bigUnitPos]
            }
          }
        }
        result += '元'
      }
      
      // 处理小数部分
      const jiao = parseInt(decPart[0] || '0')
      const fen = parseInt(decPart[1] || '0')
      
      if (jiao === 0 && fen === 0) {
        result += '整'
      } else {
        if (parseInt(intPart) === 0) {
          // 整数部分为0
          if (jiao > 0) {
            result += digits[jiao] + '角'
          } else {
            result += '零'
          }
        } else {
          if (jiao > 0) {
            result += digits[jiao] + '角'
          } else if (fen > 0) {
            result += '零'
          }
        }
        if (fen > 0) {
          result += digits[fen] + '分'
        }
      }
      
      return result
    },

    useExample(value) {
      this.amount = value
      this.convertToUppercase()
    },

    async copyResult() {
      if (!this.result) return
      
      try {
        await navigator.clipboard.writeText(this.result)
        this.showMessage(this.$t('common.copied'), 'success')
      } catch (e) {
        const textarea = document.createElement('textarea')
        textarea.value = this.result
        document.body.appendChild(textarea)
        textarea.select()
        document.execCommand('copy')
        document.body.removeChild(textarea)
        this.showMessage(this.$t('common.copied'), 'success')
      }
    },

    clearAll() {
      this.amount = ''
      this.result = ''
      this.message = ''
    },

    showMessage(msg, type) {
      this.message = msg
      this.messageType = type
      setTimeout(() => {
        this.message = ''
      }, 3000)
    }
  }
}
</script>

<style scoped>
.tool-page {
  max-width: 800px;
  margin: 0 auto;
}

.tool-header {
  text-align: center;
  margin-bottom: 32px;
}

.tool-header h1 {
  font-size: 32px;
  margin-bottom: 12px;
  color: var(--text-primary);
}

.tool-header p {
  color: var(--text-secondary);
  font-size: 16px;
}

.tool-card {
  background: var(--bg-card);
  border: 1px solid var(--border-color);
  border-radius: 16px;
  padding: 24px;
  box-shadow: var(--shadow-md);
  transition: background 0.3s, border-color 0.3s;
}

.input-section {
  margin-bottom: 24px;
}

.input-section label {
  display: block;
  font-size: 14px;
  color: var(--text-secondary);
  margin-bottom: 8px;
}

.input-wrapper {
  display: flex;
  align-items: center;
  background: var(--bg-input);
  border: 1px solid var(--border-color);
  border-radius: 8px;
  overflow: hidden;
  transition: border-color 0.2s;
}

.input-wrapper:focus-within {
  border-color: var(--accent-color);
}

.currency-symbol {
  padding: 16px;
  font-size: 24px;
  font-weight: 600;
  color: var(--text-secondary);
  background: var(--bg-tertiary);
}

.input-wrapper input {
  flex: 1;
  padding: 16px;
  font-size: 24px;
  font-weight: 600;
  border: none;
  background: transparent;
  color: var(--text-primary);
  font-family: 'Monaco', 'Menlo', monospace;
}

.input-wrapper input:focus {
  outline: none;
}

.input-wrapper input::placeholder {
  color: var(--text-muted);
  font-weight: normal;
  font-size: 16px;
}

.result-section {
  margin-bottom: 24px;
}

.result-section label {
  display: block;
  font-size: 14px;
  color: var(--text-secondary);
  margin-bottom: 8px;
}

.result-box {
  display: flex;
  align-items: center;
  background: var(--accent-light);
  border: 1px solid var(--accent-color);
  border-radius: 8px;
  padding: 16px 20px;
  min-height: 60px;
}

.result-text {
  flex: 1;
  font-size: 20px;
  font-weight: 600;
  color: var(--text-primary);
  word-break: break-all;
}

.copy-btn {
  background: transparent;
  border: none;
  font-size: 20px;
  cursor: pointer;
  padding: 8px;
  border-radius: 6px;
  transition: background 0.2s;
}

.copy-btn:hover:not(:disabled) {
  background: var(--bg-hover);
}

.copy-btn:disabled {
  opacity: 0.3;
  cursor: not-allowed;
}

.alert {
  padding: 12px 16px;
  border-radius: 8px;
  margin-bottom: 16px;
  font-size: 14px;
}

.alert.success {
  background: rgba(34, 197, 94, 0.1);
  color: var(--success-color);
  border: 1px solid rgba(34, 197, 94, 0.3);
}

.alert.error {
  background: rgba(239, 68, 68, 0.1);
  color: var(--error-color);
  border: 1px solid rgba(239, 68, 68, 0.3);
}

.actions {
  margin-bottom: 24px;
}

.btn {
  background: var(--bg-tertiary);
  color: var(--text-primary);
  border: 1px solid var(--border-color);
  border-radius: 8px;
  padding: 10px 20px;
  cursor: pointer;
  font-size: 14px;
  transition: all 0.2s;
}

.btn:hover {
  background: var(--bg-hover);
}

.examples-section {
  margin-bottom: 24px;
}

.examples-section h3 {
  font-size: 16px;
  color: var(--text-primary);
  margin-bottom: 12px;
}

.examples-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 12px;
}

.example-item {
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 12px 16px;
  background: var(--bg-tertiary);
  border: 1px solid var(--border-color);
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s;
}

.example-item:hover {
  border-color: var(--accent-color);
  transform: translateY(-2px);
}

.example-number {
  font-size: 16px;
  font-weight: 600;
  color: var(--text-primary);
  font-family: 'Monaco', 'Menlo', monospace;
}

.example-result {
  font-size: 13px;
  color: var(--text-secondary);
}

.info-section {
  background: var(--bg-tertiary);
  border-radius: 12px;
  padding: 20px;
  margin-bottom: 24px;
}

.info-section h3 {
  font-size: 16px;
  color: var(--text-primary);
  margin-bottom: 16px;
}

.table-wrapper {
  overflow-x: auto;
  margin-bottom: 16px;
}

table {
  width: 100%;
  border-collapse: collapse;
}

th, td {
  padding: 10px 8px;
  text-align: center;
  border-bottom: 1px solid var(--border-color);
  font-size: 14px;
}

th {
  background: var(--bg-secondary);
  color: var(--text-secondary);
  font-weight: 500;
}

td {
  color: var(--text-primary);
}

.unit-info {
  font-size: 14px;
  color: var(--text-secondary);
  line-height: 1.8;
}

.unit-info p {
  margin-bottom: 4px;
}

.tips-section {
  background: var(--bg-tertiary);
  border-radius: 12px;
  padding: 20px;
}

.tips-section h3 {
  font-size: 16px;
  color: var(--text-primary);
  margin-bottom: 12px;
}

.tips-section ul {
  list-style: none;
  padding: 0;
}

.tips-section li {
  font-size: 14px;
  color: var(--text-secondary);
  line-height: 1.8;
  padding-left: 20px;
  position: relative;
}

.tips-section li::before {
  content: '•';
  color: var(--accent-color);
  position: absolute;
  left: 0;
}

.tool-description {
  background: var(--bg-card);
  border: 1px solid var(--border-color);
  border-radius: 16px;
  padding: 32px;
  margin-top: 32px;
  box-shadow: var(--shadow-md);
}

.tool-description h2 {
  font-size: 20px;
  color: var(--text-primary);
  margin: 28px 0 16px;
  padding-bottom: 8px;
  border-bottom: 1px solid var(--border-color);
}

.tool-description h2:first-child {
  margin-top: 0;
}

.tool-description p {
  font-size: 15px;
  color: var(--text-secondary);
  line-height: 1.8;
  margin-bottom: 12px;
}

.tool-description ul {
  list-style: none;
  padding: 0;
  margin: 0;
}

.tool-description li {
  font-size: 15px;
  color: var(--text-secondary);
  line-height: 1.8;
  padding-left: 20px;
  position: relative;
  margin-bottom: 8px;
}

.tool-description li::before {
  content: '•';
  color: var(--accent-color);
  position: absolute;
  left: 0;
}
</style>
